import { useState } from "react";
import { Refused, api } from "../api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";

/**
 * The one thing between a stranger and a bot with write access.
 *
 * Deliberately plain. There is no account to create, nothing to recover and nobody to sign up, so
 * anything beyond a field and a button would only suggest otherwise.
 *
 * A real form, so Enter submits. Compose could not do that in a browser and said so in a comment.
 */
export function LogIn({ onIn }: { onIn: () => void }) {
  const [password, setPassword] = useState("");
  const [refused, setRefused] = useState<string | null>(null);
  const [trying, setTrying] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!password || trying) return;

    setTrying(true);
    try {
      await api.logIn(password);
      onIn();
    } catch (error) {
      // A wrong password is an answer. Anything else is worth saying plainly rather than reporting
      // as a refusal it is not.
      setRefused(
        error instanceof Refused
          ? error.message
          : "The dashboard could not be reached.",
      );
      setPassword("");
    } finally {
      setTrying(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-85">
        <CardContent>
          <form className="flex flex-col gap-5" onSubmit={submit}>
            <h1 className="font-serif text-title leading-none font-medium">
              botty
            </h1>

            <Field data-invalid={refused !== null || undefined}>
              <FieldLabel htmlFor="password" className="apparatus">
                Password
              </FieldLabel>
              <Input
                id="password"
                type="password"
                autoFocus
                autoComplete="current-password"
                value={password}
                aria-invalid={refused !== null}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setRefused(null);
                }}
              />
              {refused ? <FieldError role="alert">{refused}</FieldError> : null}
            </Field>

            <Button type="submit" disabled={!password || trying}>
              {trying ? <Spinner data-icon="inline-start" /> : null}
              {trying ? "Checking" : "Log in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
